import React, { useEffect, useState } from 'react'
import {
  CBadge,
  CButton,
  CCol,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CCloseButton,
  CPlaceholder,
  CRow,
} from '@coreui/react-pro'
import { CChart } from '@coreui/react-chartjs'
import { useChartTheme, baseChartOptions, axis, PLATFORM_META } from '../AnalyticsV2/chartTheme'
import {
  EmptyState,
  ErrorState,
  computeDelta,
  formatDateLabel,
  formatDecimal,
  formatNumber,
} from '../AnalyticsV2/common'
import RankedBarList from '../AnalyticsV2/RankedBarList'
import PlatformAnalyticsService from 'src/service/PlatformAnalyticsService'
import { deviceLabel, errorMessage, gameTypeLabel, timeAgo } from './platformHelpers'

const MINI_KPIS = [
  { key: 'active_users', label: 'Active users' },
  { key: 'sessions', label: 'Sessions' },
  { key: 'screen_views', label: 'Screen views' },
  { key: 'actions', label: 'Taps & shares' },
  { key: 'sessions_per_user', label: 'Sessions / user', format: (v) => formatDecimal(v, 1) },
  {
    key: 'avg_session_minutes',
    label: 'Avg time in app',
    format: (v) => `${formatDecimal(v, 1)} min`,
  },
]

const Loading = () => (
  <CPlaceholder component="div" animation="glow">
    <CPlaceholder xs={6} className="mb-3" />
    <CPlaceholder xs={12} size="lg" className="mb-2" style={{ height: 80 }} />
    <CPlaceholder xs={12} size="lg" style={{ height: 200 }} />
  </CPlaceholder>
)

const GameDetailOffcanvas = ({
  game,
  filters,
  reloadToken,
  live,
  onClose,
  onOpenUser,
  onOpenScreen,
  onShowUsers,
}) => {
  const theme = useChartTheme()
  const [state, setState] = useState({ data: null, loading: false, error: null })
  const [retry, setRetry] = useState(0)

  useEffect(() => {
    if (!game) return undefined
    let cancelled = false
    setState({ data: null, loading: true, error: null })
    PlatformAnalyticsService.getGame(filters, game.id)
      .then((res) => {
        if (cancelled) return
        setState(
          res?.success
            ? { data: res.data, loading: false, error: null }
            : { data: null, loading: false, error: errorMessage(res) },
        )
      })
      .catch(
        (err) => !cancelled && setState({ data: null, loading: false, error: errorMessage(err) }),
      )
    return () => {
      cancelled = true
    }
  }, [game, filters, reloadToken, retry])

  const d = state.data
  const info = d?.game
  const hasTrend = d?.trend?.some((p) => p.value > 0)
  const platforms = (d?.platforms || []).filter((p) => p.sessions > 0)
  const onlineHere = (live?.data?.people || []).filter((p) => game && p.game_id === game.id)

  return (
    <COffcanvas placement="end" visible={Boolean(game)} onHide={onClose} className="pa-offcanvas">
      <COffcanvasHeader className="border-bottom">
        <div className="min-w-0">
          <COffcanvasTitle className="text-truncate">{game?.game_name || 'Game'}</COffcanvasTitle>
          {info && (
            <div className="small text-medium-emphasis text-truncate">
              #{info.id} · {info.hub_name || 'No hub'} · {gameTypeLabel(info.game_type)}
            </div>
          )}
        </div>
        <CCloseButton className="text-reset ms-auto" onClick={onClose} />
      </COffcanvasHeader>

      <COffcanvasBody>
        {state.loading && <Loading />}
        {state.error && (
          <ErrorState message={state.error} onRetry={() => setRetry((r) => r + 1)} height={240} />
        )}

        {d && info && (
          <>
            <div className="d-flex flex-wrap gap-2 mb-3">
              <CBadge
                color={Number(info.status) === 2 ? 'secondary' : 'success'}
                shape="rounded-pill"
              >
                {Number(info.status) === 2 ? 'Season complete' : 'Season running'}
              </CBadge>
              <CBadge
                color={Number(info.game_status) === 1 ? 'primary' : 'warning'}
                shape="rounded-pill"
              >
                {Number(info.game_status) === 1 ? 'Activated' : 'Not activated'}
              </CBadge>
              <span className="av2-chip small">
                Last in app: {timeAgo(d.last_active) || 'never'}
              </span>
            </div>

            {info.admin_email && (
              <div className="small text-medium-emphasis mb-3">
                Admin: <span className="text-body">{info.admin_name || '—'}</span> ·{' '}
                <a href={`mailto:${info.admin_email}`}>{info.admin_email}</a>
              </div>
            )}

            <div className="d-flex flex-wrap gap-2 mb-3">
              <CButton color="primary" size="sm" onClick={() => onShowUsers(info.id)}>
                See this game&apos;s app users
              </CButton>
            </div>

            <div className="pa-mini mb-3">
              <div className="d-flex align-items-center gap-2 mb-1">
                <span className="pa-online-dot" aria-hidden="true" />
                <strong>{formatNumber(onlineHere.length)} online now</strong>
              </div>
              {onlineHere.length > 0 ? (
                <ul className="pa-live-list">
                  {onlineHere.slice(0, 8).map((p) => (
                    <li key={p.user_id}>
                      <button
                        type="button"
                        className="pa-live-row"
                        onClick={() => onOpenUser({ id: p.user_id, full_name: p.full_name })}
                      >
                        <span className="text-truncate">{p.full_name || `User #${p.user_id}`}</span>
                        <span className="small text-medium-emphasis text-truncate">
                          {deviceLabel(p.device_type)} · {p.current_screen || '—'}
                        </span>
                      </button>
                    </li>
                  ))}
                  {onlineHere.length > 8 && (
                    <li className="small text-medium-emphasis">
                      +{onlineHere.length - 8} more — see the Live tab
                    </li>
                  )}
                </ul>
              ) : (
                <div className="small text-medium-emphasis">
                  Nobody from this game is in the app.
                </div>
              )}
            </div>

            <CRow className="g-2 mb-3">
              <CCol xs={6}>
                <div className="pa-mini">
                  <div className="small text-medium-emphasis">Members</div>
                  <div className="fs-5 fw-semibold">{formatNumber(d.members)}</div>
                  <div className="small text-success">
                    {d.new_members > 0
                      ? `+${formatNumber(d.new_members)} this period`
                      : 'No new joins'}
                  </div>
                </div>
              </CCol>
              {MINI_KPIS.map((k) => {
                const delta = computeDelta(d.current[k.key], d.previous[k.key])
                return (
                  <CCol xs={6} key={k.key}>
                    <div className="pa-mini">
                      <div className="small text-medium-emphasis">{k.label}</div>
                      <div className="fs-5 fw-semibold">
                        {(k.format || formatNumber)(d.current[k.key])}
                      </div>
                      <div className={`small text-${delta.color}`}>{delta.label}</div>
                    </div>
                  </CCol>
                )
              })}
            </CRow>

            <h6 className="mb-2">Daily active users</h6>
            {hasTrend ? (
              <div className="av2-chart pa-chart--drawer mb-4">
                <CChart
                  customTooltips={false}
                  type="line"
                  data={{
                    labels: d.trend.map((p) => formatDateLabel(p.bucket)),
                    datasets: [
                      {
                        label: 'Active users',
                        data: d.trend.map((p) => p.value),
                        borderColor: theme.series[0],
                        backgroundColor: `${theme.series[0]}22`,
                        fill: true,
                        borderWidth: 2,
                        pointRadius: 0,
                        pointHoverRadius: 5,
                        tension: 0.3,
                      },
                    ],
                  }}
                  options={baseChartOptions(theme, {
                    plugins: { legend: { display: false } },
                    scales: {
                      x: axis(theme, { grid: { display: false }, ticks: { maxTicksLimit: 6 } }),
                      y: axis(theme, { beginAtZero: true, ticks: { precision: 0 } }),
                    },
                  })}
                />
              </div>
            ) : (
              <EmptyState
                height={140}
                title="No app activity in this period"
                hint="Nobody opened the app for this game in the selected range."
              />
            )}

            {platforms.length > 0 && (
              <>
                <h6 className="mb-2">Devices</h6>
                <div className="pa-split mb-1" aria-hidden="true">
                  {platforms.map((p) => {
                    const meta = PLATFORM_META.find((m) => m.key === p.device_type)
                    return (
                      <span
                        key={p.device_type}
                        style={{
                          width: `${p.share_pct}%`,
                          background: meta ? theme.series[meta.slot] : theme.neutral,
                        }}
                      />
                    )
                  })}
                </div>
                <div className="d-flex flex-wrap gap-3 small mb-4">
                  {platforms.map((p) => {
                    const meta = PLATFORM_META.find((m) => m.key === p.device_type)
                    return (
                      <span key={p.device_type} className="d-inline-flex align-items-center gap-1">
                        <span
                          className="av2-swatch"
                          style={{ background: meta ? theme.series[meta.slot] : theme.neutral }}
                        />
                        {meta ? meta.label : 'Unknown'} · {formatDecimal(p.share_pct, 0)}% (
                        {formatNumber(p.users)} users)
                      </span>
                    )
                  })}
                </div>
              </>
            )}

            <h6 className="mb-2">Most viewed screens</h6>
            <div className="mb-4">
              <RankedBarList
                rows={d.top_screens.map((s) => ({
                  key: s.page_name,
                  label: s.page_name,
                  value: s.views,
                  people: s.users,
                }))}
                color={theme.series[0]}
                valueLabel="views"
                emptyLabel="No screen views in this period."
                onRowClick={(row) => onOpenScreen(row.key)}
              />
            </div>

            <h6 className="mb-2">Top actions</h6>
            <RankedBarList
              rows={d.top_actions.map((a) => ({
                key: a.event_name,
                label: a.event_name,
                value: a.count,
                people: a.users,
              }))}
              color={theme.series[1]}
              valueLabel="times"
              emptyLabel="No taps or shares in this period."
            />
          </>
        )}
      </COffcanvasBody>
    </COffcanvas>
  )
}

export default GameDetailOffcanvas
